import { readonly, ref } from 'vue'
import type { AuthSession, PortalPreviewContext } from '@/api/types'

const STORAGE_KEY = 'avoschool.portal.preview'
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const active = ref(false)
const phase = ref<'pending' | 'ready' | 'ended'>('ended')
const context = ref<PortalPreviewContext | null>(null)
const message = ref('Откройте личный кабинет из карточки ученика или преподавателя в бэкофисе.')
let previewId: string | null = null
let handoffToken: string | null = null
let expiryTimer: ReturnType<typeof setTimeout> | undefined

// Only a non-secret id is persisted, in this tab. The credential is an HttpOnly
// cookie scoped to this id; SESSION and other preview tabs are never replaced.
function persist(): void {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ id: previewId, ready: phase.value === 'ready' })) }
  catch { /* Preview still works until this tab is reloaded. */ }
}

/** Called by the portal entry point only, never by the backoffice bundle. */
export function initializePortalPreview(): void {
  if (window.location.pathname === '/admin-preview' && window.location.hash) {
    const params = new URLSearchParams(window.location.hash.slice(1))
    const id = params.get('id') ?? ''
    handoffToken = params.get('token')
    // Remove the handoff before routing, analytics, or any asynchronous work.
    window.history.replaceState(window.history.state, '', '/admin-preview')
    active.value = true
    previewId = UUID.test(id) ? id : null
    phase.value = previewId && handoffToken ? 'pending' : 'ended'
    persist()
    return
  }
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null') as { id?: string; ready?: boolean } | null
    if (saved) {
      active.value = true
      previewId = saved.id && UUID.test(saved.id) ? saved.id : null
      phase.value = saved.ready && previewId ? 'ready' : 'ended'
    }
  } catch { /* Invalid storage must not select a target account. */ }
}

export const portalPreviewState = {
  active: readonly(active), phase: readonly(phase), context: readonly(context), message: readonly(message),
}
export function isPortalPreviewActive(): boolean { return active.value }

export class PortalPreviewReadonlyError extends Error {
  constructor() { super('В режиме просмотра администратора изменения недоступны.'); this.name = 'PortalPreviewReadonlyError' }
}

function lifecyclePath(path: string): boolean {
  return Boolean(previewId && (path === `/portal-previews/${previewId}/activate` || path === `/portal-previews/${previewId}/close`))
}
export function assertPortalPreviewRequest(path: string, method = 'GET'): void {
  if (!active.value) return
  if (path === '/auth/csrf' && method === 'GET') return
  if (lifecyclePath(path) && method === 'POST') return
  if (phase.value !== 'ready' || !previewId) throw new Error(message.value)
  if (!['GET', 'HEAD'].includes(method)
    || !(path === '/auth/me' || path.startsWith('/portal/'))
    || /\/share-link(?:\?|$)/.test(path)) throw new PortalPreviewReadonlyError()
}

export function portalPreviewApiPath(path: string): string {
  if (!active.value || path === '/auth/csrf' || lifecyclePath(path)) return path
  // Never silently fall back to the normal cookie if the preview has expired.
  return `/portal-previews/${previewId ?? 'unavailable'}${path}`
}

export function endPortalPreview(reason = 'Просмотр завершён. Чтобы открыть кабинет снова, используйте кнопку в бэкофисе.'): void {
  if (!active.value) return
  clearTimeout(expiryTimer)
  handoffToken = null
  context.value = null
  phase.value = 'ended'
  message.value = reason
  persist()
}

export function acceptPortalPreviewSession(session: AuthSession): void {
  if (!active.value) return
  const value = session.portalPreview
  if (!value || value.id !== previewId || !value.readOnly || !Number.isFinite(Date.parse(value.expiresAt))) {
    endPortalPreview('Не удалось подтвердить режим просмотра. Откройте кабинет заново из бэкофиса.')
    throw new Error(message.value)
  }
  context.value = value
  clearTimeout(expiryTimer)
  const remaining = Date.parse(value.expiresAt) - Date.now()
  const expire = () => {
    endPortalPreview('Время просмотра истекло. Откройте кабинет заново из бэкофиса.')
    window.dispatchEvent(new CustomEvent('avoschool:auth-required'))
  }
  if (remaining <= 0) { expire(); throw new Error(message.value) }
  expiryTimer = setTimeout(expire, Math.min(remaining, 2_147_483_647))
}

export async function activatePortalPreview(): Promise<void> {
  if (phase.value !== 'pending' || !previewId || !handoffToken) return
  const token = handoffToken
  handoffToken = null
  const { api } = await import('@/api/http')
  try {
    await api.post(`/portal-previews/${previewId}/activate`, { token })
    phase.value = 'ready'
    persist()
  } catch (error) {
    endPortalPreview(error instanceof Error ? error.message : 'Не удалось открыть просмотр. Повторите вход из бэкофиса.')
    throw error
  }
}

export async function closePortalPreview(): Promise<void> {
  try {
    if (previewId && phase.value === 'ready') {
      const { api } = await import('@/api/http')
      await api.post(`/portal-previews/${previewId}/close`)
    }
  } finally { endPortalPreview() }
}
